// Export the generic preview image generator
export const GenerateGenericPreviewImage = ({
    shape = "square",
    fillColour = "#2563EB",
}) => {

    // Set the default square image width
    let imageWidth = 1024;

    // Set the default square image height
    let imageHeight = 1024;



    // Use ISO A-series portrait dimensions for portrait previews
    if (shape === "isoPortrait") {

        // Set the portrait width to approximately 1 divided by square root of 2
        imageWidth = 724;

        // Set the portrait height
        imageHeight = 1024;

    }



    // Use ISO A-series landscape dimensions for landscape previews
    if (shape === "isoLandscape") {

        // Set the landscape width
        imageWidth = 1024;

        // Set the landscape height to approximately 1 divided by square root of 2
        imageHeight = 724;

    }



    // Use a two-to-one portrait ratio for the tall rectangle
    if (shape === "tallRectangle") {

        // Set the tall rectangle width
        imageWidth = 512;

        // Set the tall rectangle height
        imageHeight = 1024;

    }



    // Use a two-to-one landscape ratio for the wide rectangle
    if (shape === "wideRectangle") {

        // Set the wide rectangle width
        imageWidth = 1024;

        // Set the wide rectangle height
        imageHeight = 512;

    }



    // Create a canvas for the generated PNG
    const canvas = document.createElement("canvas");

    // Set the generated image width
    canvas.width = imageWidth;

    // Set the generated image height
    canvas.height = imageHeight;

    // Get the two-dimensional drawing context
    const context = canvas.getContext("2d");

    // Stop if the browser could not create the drawing context
    if (!context) {

        throw new Error("Could not create the generic preview image canvas");

    }

    // Keep the canvas transparent until the selected shape is drawn
    context.clearRect(0, 0, imageWidth, imageHeight);

    // Enable smooth shape edges
    context.imageSmoothingEnabled = true;

    // Set the selected shape colour
    context.fillStyle = fillColour || "#2563EB";



    // Draw a circle on the transparent square canvas
    if (shape === "circle") {

        // Calculate the circle centre
        const centreX = imageWidth / 2;
        const centreY = imageHeight / 2;

        // Calculate the largest radius that fits inside the canvas
        const radius = Math.min(imageWidth, imageHeight) / 2;

        // Draw the circle
        context.beginPath();
        context.arc(centreX, centreY, radius, 0, Math.PI * 2);
        context.closePath();
        context.fill();

    }



    // Draw a triangle on the transparent square canvas
    if (shape === "triangle") {

        // Start the triangle path at the top centre
        context.beginPath();
        context.moveTo(imageWidth / 2, 0);

        // Draw to the bottom-right corner
        context.lineTo(imageWidth, imageHeight);

        // Draw to the bottom-left corner
        context.lineTo(0, imageHeight);

        // Close and fill the triangle
        context.closePath();
        context.fill();

    }



    // Draw all rectangular shape and aspect presets
    if (
        shape === "square" ||
        shape === "isoPortrait" ||
        shape === "isoLandscape" ||
        shape === "tallRectangle" ||
        shape === "wideRectangle"
    ) {

        // Fill the complete canvas
        context.fillRect(0, 0, imageWidth, imageHeight);

    }



    // Reject unsupported shapes rather than silently substituting another preset
    if (
        shape !== "square" &&
        shape !== "circle" &&
        shape !== "triangle" &&
        shape !== "isoPortrait" &&
        shape !== "isoLandscape" &&
        shape !== "tallRectangle" &&
        shape !== "wideRectangle"
    ) {

        throw new Error(
            "Unsupported generic preview shape: " + String(shape || "")
        );

    }



    // Convert the completed canvas into a PNG data URL accepted by the image bucket
    const imageDataUrl = canvas.toDataURL("image/png");



    // Return the generated image payload
    return {

        // Store image details for the picker
        imageDetails: {

            // Store the generated filename
            name: `generic-${shape}-preview.png`,

            // Store the generated image URL
            image_url: imageDataUrl,

        },

        // Store the generated image for saving
        uploadedImage: imageDataUrl,

        // Identify the generic preview mode
        previewMode: "generic",

        // Allow the image to be used as a product preview
        suppressProductPreview: false,

    };

};





// Export the transparent image generator used by the No Image option
export const GenerateBlankPreviewPlaceholderImage = ({

    // Canvas size
    size = 512,

} = {}) => {

    // Create a canvas for the transparent placeholder image
    const canvas = document.createElement("canvas");

    // Set the canvas width
    canvas.width = size;

    // Set the canvas height
    canvas.height = size;

    // Get the two-dimensional drawing context
    const context = canvas.getContext("2d");

    // Stop if the browser could not create the drawing context
    if (!context) {

        throw new Error("Could not create the blank preview image canvas");

    }

    // Leave every pixel transparent
    context.clearRect(0, 0, size, size);

    // Convert the transparent canvas into a valid PNG data URL
    const dataUrl = canvas.toDataURL("image/png");

    // Return a normal image payload so the role still receives an asset id
    return {

        imageDetails: {
            name: "blank-preview-placeholder.png",
            image_url: dataUrl,
        },

        uploadedImage: dataUrl,

        previewMode: "none",

        suppressProductPreview: true,

    };

};